import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IUser, UserPermissionsEnum, UserRolesEnum } from 'src/modules/shared/models/interfaces/users.interface';
import { environment } from 'src/environments/environment';
import { map, share, switchMap } from 'rxjs/operators';
import Fingerprint from 'fingerprintjs2';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUserSubject: BehaviorSubject<IUser>;
  private _helper = new JwtHelperService();
  private _sessionUID: string;
  private _userFingerprint: string;

  constructor(private _jwtHelper: JwtHelperService, private _http: HttpClient) {
    this._sessionUID = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

    this.currentUserSubject = new BehaviorSubject<IUser>(<IUser>JSON.parse(localStorage.getItem('token')));

    if (!this.currentUserFingerprint) {
      this.generateUserFingerprint();
    }
  }

  public currentUserValue(token?: string): IUser {
    if (!token && this.currentUserSubject?.value) {
      token = this.currentUserSubject?.value['access_token'];
    }

    if (token) {
      const u = this._helper.decodeToken<IUser>(token);
      u.sessionUID = this._sessionUID;
      u.ufingerprint = this.currentUserFingerprint;
      u.systemVersion = environment.version;
      return u;
    }

    return null;
  }

  public get currentTokenValue(): string {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(token) : null;
  }

  public get currentUserFingerprint(): string {
    return this._userFingerprint || localStorage.getItem('userFingerprint');
  }

  public isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return token ? !this._jwtHelper.isTokenExpired(<string>JSON.parse(token)['access_token']) : false;
  }

  public async generateUserFingerprint(): Promise<string> {
    const options = {};
    return await Fingerprint.getPromise(options).then((components) => {
      const values = components.map((component) => component.value);
      this._userFingerprint = Fingerprint.x64hash128(values.join(''), 31);
      localStorage.setItem('userFingerprint', this._userFingerprint);
      return this._userFingerprint;
    });
  }

  private _toFormUrlEncoded(str: string): string {
    return encodeURIComponent(str)
      .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16))
      .replace(/%20/g, '+');
  }

  public authenticate(username: string, password: string): Observable<IUser | unknown> {
    const headers = new HttpHeaders();

    const data = `username=${this._toFormUrlEncoded(username)}&password=${this._toFormUrlEncoded(password)}&grant_type=password&client_id=${
      environment.clientId
    }&client_secret=${environment.clientSecret}`;

    const options = {
      headers: headers,
      withCredentials: false,
    };

    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    if (username.split('@nibo.com.br').length !== 2) {
      return throwError('Este login não tem permissão para acessar o sistema.');
    }

    return this._http.post<IUser>(`${environment.passportUrl}/token`, data, options).pipe(
      switchMap((user) => {
        if (user && user.access_token) {
          if (!this.currentUserValue(JSON.stringify(user.access_token)).RobotsRole) {
            return throwError('Você não tem permissão para acessar o sistema.');
          }

          localStorage.setItem('token', JSON.stringify(user));
          this.currentUserSubject.next(user);
        }

        return of(user);
      })
    );
  }

  /**
   * Possibilita a inserção do token "on the fly".
   * Útil especialmente para web-components que dependem de serviços.
   * @param token Token de autenticação do NIBO
   */
  public injectToken(token: string): Observable<void> {
    localStorage.setItem('token', JSON.stringify({ access_token: token }));
    return of(null);
  }

  public refreshToken(): Observable<IUser> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    const options = {
      headers: headers,
      withCredentials: false,
    };

    const token = this.currentTokenValue;

    if (!token || !token['refresh_token']) {

      return throwError(() => new Error('Token inválido'));
    }
    
    const data = `grant_type=refresh_token&client_id=${environment.clientId}&client_secret=${environment.clientSecret}&refresh_token=${token['refresh_token']}`;
    return this._http.post<any>(`${environment.passportUrl}/token`, data, options).pipe(
      share(),
      map<IUser, IUser>((user) => {
        if (user && user.access_token) {
          localStorage.setItem('token', JSON.stringify(user));
          this.currentUserSubject.next(user);
        }

        return user;
      })
    );
  }

  public hasPermission(feature: UserPermissionsEnum): Observable<boolean> {
    return this.currentUserSubject.pipe(
      map((user) => {
        let hasPermission = false;

        if (user) {
          switch (feature) {
            case UserPermissionsEnum.createRobot:
              switch (this.currentUserValue().RobotsRole) {
                case UserRolesEnum.superAdmin:
                case UserRolesEnum.admin:
                case UserRolesEnum.robotsManager:
                  hasPermission = true;
                  break;
              }
              break;
          }
        }

        return hasPermission;
      })
    );
  }

  public logout(): void {
    localStorage.removeItem('token');
    this.currentUserSubject.next(null);
  }
}
